import { useState } from "react";
import { Trash2, XCircle } from "lucide-react";
import { cancelBook, deleteBook, type Book } from "../../api/pqx";
import { useMe } from "../../components/MeProvider";
import { useModal } from "../../components/ModalProvider";
import { RUNNING_STATES } from "./bookStates";

/**
 * Who may remove a book, and when: its uploader or an admin, once the engine
 * has finished with it. A running book cannot be removed (the engine refuses).
 */
export function canRemoveBook(
  book: Pick<Book, "reviewer" | "state">,
  me: { id: string; can: { admin: boolean } } | null | undefined,
): boolean {
  if (RUNNING_STATES.has(book.state)) return false;
  return me?.id === book.reviewer || Boolean(me?.can.admin);
}

/**
 * Who may cancel a book: its uploader or an admin, while the engine is still
 * on it — queued, converting, extracting or checking. Not once it is "done"
 * and being imported: that takes seconds, and is then removed instead.
 */
export function canCancelBook(
  book: Pick<Book, "reviewer" | "state">,
  me: { id: string; can: { admin: boolean } } | null | undefined,
): boolean {
  if (!CANCELLABLE.has(book.state)) return false;
  return me?.id === book.reviewer || Boolean(me?.can.admin);
}

const CANCELLABLE = new Set<Book["state"]>([
  "queued",
  "converting",
  "extracting",
  "checking_answers",
]);

/**
 * "Cancel extraction": stops a queued or running book, after confirming. The
 * engine's work on it is lost; the book stays in the list as Cancelled, and
 * the same file can be uploaded again.
 */
export function CancelBookButton({
  book,
  onCancelled,
  compact = false,
}: {
  book: Book;
  onCancelled: (book: Book) => void;
  compact?: boolean;
}) {
  const { me } = useMe();
  const modal = useModal();
  const [busy, setBusy] = useState(false);
  if (!canCancelBook(book, me)) return null;

  const cancel = async () => {
    const ok = await modal.showConfirm(
      `Cancel “${book.filename}”?`,
      book.state === "queued"
        ? "It won't be extracted. You can upload the same file again later."
        : "The engine stops working on it, and what it has extracted so far is lost. You can upload the same file again later.",
    );
    if (!ok) return;
    setBusy(true);
    try {
      onCancelled(await cancelBook(book.book_id));
    } catch (e) {
      modal.showError("Couldn't cancel", e instanceof Error ? e.message : "Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void cancel()}
      disabled={busy}
      aria-label={compact ? `Cancel ${book.filename}` : undefined}
      className={
        compact
          ? "inline-flex shrink-0 items-center gap-1 rounded-md border border-negative/50 px-2 py-1 text-[12px] font-semibold text-negative hover:bg-negative/10 disabled:opacity-60"
          : "inline-flex items-center gap-1.5 rounded-lg border border-negative bg-negative/10 px-3 py-1.5 text-[13px] font-semibold text-negative hover:bg-negative/20 disabled:opacity-60"
      }
    >
      <XCircle size={compact ? 13 : 14} aria-hidden="true" />
      {busy ? "Cancelling…" : compact ? "Cancel" : "Cancel extraction"}
    </button>
  );
}

/**
 * "Remove book from the engine", in orange so it is found, and confirmed
 * before anything happens. The upload and the engine's working files go;
 * questions already in the bank stay.
 *
 * `compact` is the books-list version: a small button beside the status.
 */
export function RemoveBookButton({
  book,
  onRemoved,
  compact = false,
}: {
  book: Book;
  onRemoved: () => void;
  compact?: boolean;
}) {
  const { me } = useMe();
  const modal = useModal();
  if (!canRemoveBook(book, me)) return null;

  const remove = async () => {
    const ok = await modal.showConfirm(
      `Remove “${book.filename}”?`,
      "The upload and the engine's working files are deleted. Questions already in the bank stay.",
    );
    if (!ok) return;
    try {
      await deleteBook(book.book_id);
      onRemoved();
    } catch (e) {
      modal.showError("Couldn't remove", e instanceof Error ? e.message : "Try again.");
    }
  };

  return (
    <button
      type="button"
      onClick={() => void remove()}
      aria-label={compact ? `Remove ${book.filename}` : undefined}
      className={
        compact
          ? "inline-flex shrink-0 items-center gap-1 rounded-md border border-warning/50 px-2 py-1 text-[12px] font-semibold text-warning hover:bg-warning/10"
          : "inline-flex items-center gap-1.5 rounded-lg border border-warning bg-warning/10 px-3 py-1.5 text-[13px] font-semibold text-warning hover:bg-warning/20"
      }
    >
      <Trash2 size={compact ? 13 : 14} aria-hidden="true" />
      {compact ? "Remove" : "Remove book from the engine"}
    </button>
  );
}
